import React from 'react';
import styled from 'styled-components';
import uniqueId from 'lodash-es/uniqueId';
import { useNavigate } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '@/store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { getNumberInfo } from '@/page-components/utils/functions';
import { fetchStatus, activate, clearAirTimerStatus } from '@/store/slices/airTimer';

//import './index.scss';

type AirTimerProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const AirTimer = (componentProps: AirTimerProps) => {
  let props = componentProps;

  const counterId = React.useRef(uniqueId('air-timer-counter-'));
  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);
  const authentication = useAppSelector((state) => state.authentication);
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.airTimer.status);
  const auth_type = useAppSelector((state) => state.airTimer.auth_type);
  const navigate = useNavigate();

  const currentValue = React.useRef(0);
  const startValue = React.useRef(0);
  const finalValue = React.useRef(0);
  const lerpStartTime = React.useRef(new Date().valueOf());

  React.useEffect(() => {
    if (authentication.auth_type !== auth_type) {
      dispatch(fetchStatus());
    }
  }, [authentication.auth_type, auth_type]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (status?.counter_value) {
      finalValue.current = status.counter_value;
      startValue.current = currentValue.current;
      lerpStartTime.current = new Date().valueOf();
    }
  }, [status?.counter_value]);

  const updateNumbers = React.useCallback((value: any, selector: any) => {
    let numberInfo = getNumberInfo({ amount: value, digits: 0 })?.amount;
    numberInfo = numberInfo.replace(/.00$/g, '');
    const elements = document.querySelectorAll(selector);
    elements.forEach((element: any) => {
      if (element) {
        element.innerHTML = numberInfo;
      }
    });
  }, []);

  const toNumber = React.useCallback((value: string, def: number) => {
    if (value) {
      const number = parseInt(value.replace(/[^0-9]/g, ''), 10);
      if (isNaN(number)) {
        return def;
      }
      return number;
    }
    return def;
  }, []);

  const lerp = (x: number, y: number, a: number) => {
    return Math.floor(x * (1 - a) + y * a);
  };

  React.useEffect(() => {
    const selector = `.${counterId.current}`;

    const countDownTimer = setInterval(
      () => {
        if (currentValue.current === 0 && currentValue.current < finalValue.current) {
          startValue.current = finalValue.current + toNumber(props.properties.offsetCount, 20);
          currentValue.current = finalValue.current + toNumber(props.properties.offsetCount, 20);
          updateNumbers(currentValue.current, selector);
          return;
        }

        const start = lerpStartTime.current;
        const end = lerpStartTime.current + toNumber(props.properties.updateIntervalMs, 10 * 1000);
        const now = new Date().valueOf();
        const delta = (now - start) / (end - start);

        if (delta > 1) {
          return;
        }
        currentValue.current = lerp(startValue.current, finalValue.current, delta);

        /*
        console.log(
          'AirTimer[lerp]',
          startValue.current,
          currentValue.current,
          finalValue.current,
          (now - start) / (end - start),
        );
        */

        updateNumbers(currentValue.current, selector);
      },
      toNumber(props.properties.updateTimeMs, 100),
    );

    return () => clearInterval(countDownTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPlay = React.useCallback(() => {
    if (status?.game_id != null && status?.game_id) {
      navigate(`/slot-game/${status.game_id}/free-spin-game`);
      return;
    }
    navigate('/profile/wallet');
  }, [status, navigate]);

  const onActivate = React.useCallback(() => {
    if (['user', 'token'].indexOf(authentication.auth_type) === -1 || authentication.access_token === null) {
      navigate('/login');
      return;
    }

    if (status?.id) {
      dispatch(activate({ id: status.id })).then((resp) => {
        console.log('resp', resp);
        navigate('/profile/wallet');
      });
    }
  }, [dispatch, status, navigate, authentication]);

  const onExpire = React.useCallback(() => {
    dispatch(clearAirTimerStatus());
  }, [dispatch]);

  const contextValue = React.useMemo(() => {
    return {
      counterId: counterId.current,
      startDate: new Date(status?.start_date ?? 0).valueOf(),
      endDate: new Date(status?.end_date ?? 0).valueOf(),
      activated: status?.collected,
      collecting: status?.collecting,
      cms_details: status?.cms_details,
      art_bundle: status?.art_bundle,
      game_id: status?.game_id,
      status: status?.status,
      actions: {
        onActivate,
        onPlay,
        onExpire,
      },
      parentContext: dataElementContext,
    };
  }, [counterId, status, onPlay, onActivate, onExpire, dataElementContext]);

  //console.log('AirTimer[contextValue]', contextValue);
  if (!contextValue?.status || contextValue?.status === 'finished') {
    currentValue.current = 0;
    startValue.current = 0;
    finalValue.current = 0;
    return null;
  }

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default AirTimer;
